const LEGACY_TOKEN_PARAM = 'appaccesstoken';

// Session-storage keys written by earlier releases that could hold the retired token.
const STOWAGE_PARAMS_STORAGE_KEY = 'lastStowageParams';
const POST_LOGIN_REDIRECT_STORAGE_KEY = 'postLoginRedirect';

const isLegacyTokenKey = (rawKey: string): boolean => {
    let key = rawKey.replace(/\+/g, ' ');
    try {
        key = decodeURIComponent(key);
    } catch {
        // Malformed escape sequence: compare the raw key.
    }
    return key.trim().toLowerCase() === LEGACY_TOKEN_PARAM;
};

const isLegacyTokenSegment = (segment: string): boolean => isLegacyTokenKey(segment.split('=')[0]);

/**
 * Removes the retired `appAccessToken` query parameter (any casing, repeated or empty) from the
 * current URL via history.replaceState. The URL is only rewritten when the token is present;
 * all other parameters and the hash are preserved untouched. No reload is triggered.
 */
export const sanitizeLegacyAppAccessToken = (win: Window = window): boolean => {
    const { pathname, search, hash } = win.location;
    if (!search || search.length < 2) return false;

    const segments = search.slice(1).split('&');
    const remaining = segments.filter((segment) => !isLegacyTokenSegment(segment));
    if (remaining.length === segments.length) return false;

    const query = remaining.filter((segment) => segment !== '').join('&');
    win.history.replaceState(win.history.state, '', `${pathname}${query ? `?${query}` : ''}${hash}`);
    return true;
};

const stripLegacyTokenFromQuery = (query: string): string =>
    query
        .split('&')
        .filter((segment) => segment !== '' && !isLegacyTokenSegment(segment))
        .join('&');

const stripLegacyTokenFromPath = (path: string): string => {
    const hashIndex = path.indexOf('#');
    const beforeHash = hashIndex === -1 ? path : path.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : path.slice(hashIndex);
    const queryIndex = beforeHash.indexOf('?');
    if (queryIndex === -1) return path;

    const query = stripLegacyTokenFromQuery(beforeHash.slice(queryIndex + 1));
    return `${beforeHash.slice(0, queryIndex)}${query ? `?${query}` : ''}${hash}`;
};

// Conservative check for values that cannot be parsed: the key may be literal, percent-encoded or JSON-escaped.
const mayContainLegacyToken = (raw: string): boolean => /appaccesstoken|%|\\u/i.test(raw);

const sanitizeStowageParams = (storage: Storage): void => {
    const raw = storage.getItem(STOWAGE_PARAMS_STORAGE_KEY);
    if (!raw) return;

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        if (mayContainLegacyToken(raw)) storage.removeItem(STOWAGE_PARAMS_STORAGE_KEY);
        return;
    }

    if (!Array.isArray(parsed)) {
        if (mayContainLegacyToken(raw)) storage.removeItem(STOWAGE_PARAMS_STORAGE_KEY);
        return;
    }

    const remaining = parsed.filter((entry) => !(Array.isArray(entry) && typeof entry[0] === 'string' && isLegacyTokenKey(entry[0])));
    if (remaining.length === parsed.length) return;

    if (remaining.length === 0) storage.removeItem(STOWAGE_PARAMS_STORAGE_KEY);
    else storage.setItem(STOWAGE_PARAMS_STORAGE_KEY, JSON.stringify(remaining));
};

const sanitizePostLoginRedirect = (storage: Storage): void => {
    const raw = storage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    if (!raw) return;

    let parsed: { path?: unknown } | null;
    try {
        parsed = JSON.parse(raw) as { path?: unknown } | null;
    } catch {
        if (mayContainLegacyToken(raw)) storage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
        return;
    }

    if (!parsed || typeof parsed.path !== 'string') {
        if (mayContainLegacyToken(raw)) storage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
        return;
    }

    const path = stripLegacyTokenFromPath(parsed.path);
    if (path === parsed.path) return;

    storage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, JSON.stringify({ ...parsed, path }));
};

const STORAGE_NAMES = ['sessionStorage', 'localStorage'] as const;
const CLEANUPS: ReadonlyArray<{ key: string; run: (storage: Storage) => void }> = [
    { key: STOWAGE_PARAMS_STORAGE_KEY, run: sanitizeStowageParams },
    { key: POST_LOGIN_REDIRECT_STORAGE_KEY, run: sanitizePostLoginRedirect },
];

/**
 * Removes the retired `appAccessToken` from values persisted by earlier releases
 * (`lastStowageParams`, `postLoginRedirect`) so SPA restoration cannot bring it back.
 * Literal, case-varied, repeated, empty and percent-encoded keys are all removed regardless of how
 * the stored value spells the name. Supported params such as `imoNumber` are kept.
 * Unavailable storage is skipped silently; a failing cleanup logs only the store and key name
 * (never a stored value or error message) and does not stop the remaining cleanups.
 */
export const sanitizeLegacyPersistedState = (win: Window = window): void => {
    STORAGE_NAMES.forEach((name) => {
        let storage: Storage | undefined;
        try {
            storage = win[name];
        } catch {
            // Storage unavailable (e.g. blocked by the browser): nothing to sanitize.
            return;
        }
        if (!storage) return;

        CLEANUPS.forEach(({ key, run }) => {
            try {
                run(storage as Storage);
            } catch {
                console.warn(`Legacy token cleanup failed for ${name}:${key}`);
            }
        });
    });
};